import React, { useState, useEffect } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import printService from '../../../services/printService';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { generateBillContent, DEFAULT_RECEIPT_CONFIG } from '../../../utils/receiptFormatter';

export default function ReprintReceiptModal({ bill, onClose }) {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  const [branchDetails, setBranchDetails] = useState(null);

  // Fetch default receipt configuration and branch details
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [configResult, branchResult] = await Promise.all([
          receiptConfigurationService.getDefault('BILL').catch(err => {
            console.warn('Failed to fetch receipt configuration, using defaults:', err);
            return null;
          }),
          branchService.getCurrentBranch().catch(err => {
            console.warn('Failed to fetch branch details:', err);
            return null;
          })
        ]);

        if (configResult) {
          setReceiptConfig(configResult);
        }
        if (branchResult) {
          setBranchDetails(branchService.formatBranchForReceipt(branchResult));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  if (!bill) return null;

  // Group items from all orders in the bill
  const groupedItems = {};
  (bill.orders || []).forEach(order => {
    (order.items || []).forEach(item => {
      const key = item.itemName || item.name;
      if (!groupedItems[key]) {
        groupedItems[key] = {
          name: key,
          quantity: 0,
          price: item.unitPrice || item.price || 0,
          total: 0
        };
      }
      groupedItems[key].quantity += item.quantity || 1;
      groupedItems[key].total += (item.unitPrice || item.price || 0) * (item.quantity || 1);
    });
  });

  const receiptItems = Object.values(groupedItems);

  // Use printer configuration
  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;

  // Format timestamp
  const formatDateTime = (dateString) => {
    if (!dateString) return new Date().toLocaleString('en-IN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  // Raw bill timestamp — POS DTOs are camelCase, bills-list rows are snake_case.
  // Kept unformatted so the receipt formatter can parse it; `timestamp` below is
  // the display string for the browser-print fallback only.
  const billTimestampRaw =
    bill.finalizedAt || bill.finalized_at || bill.createdAt || bill.created_at || null;
  const timestamp = formatDateTime(billTimestampRaw);

  // Reprints keep the original order type; falls back to the formatter's
  // table-name check when the bill doesn't carry one.
  const rawOrderType = bill.orderType || bill.order_type || null;
  const orderTypeLabel = rawOrderType
    ? (String(rawOrderType).toUpperCase().includes('TAKE') ? 'Takeaway' : 'Dine In')
    : undefined;

  // Extract amounts from bill
  const subtotal = bill.subtotal || 0;
  const discountAmount = bill.discount_amount || bill.discountAmount || 0;
  const discountPercentage = bill.discount_percentage || bill.discountPercentage || 0;
  const taxableAmount = bill.taxable_amount || bill.taxableAmount || 0;
  const taxAmount = bill.tax_amount || bill.taxAmount || 0;
  const taxPercentage = bill.tax_percentage || bill.taxPercentage || 0;
  const totalAmount = bill.total_amount || bill.totalAmount || 0;
  const roundOff = bill.round_off || bill.roundOff || 0;
  const finalAmount = bill.final_amount || bill.finalAmount || 0;
  const billNumber = bill.billNumber || bill.bill_number || 'N/A';

  // Raw table name from the bill (internal system name — may be MAVERICK_COUNTER
  // for counter/takeaway orders).
  const rawTableName = bill.tableName || `Table ${bill.table_number || bill.tableNumber || 'N/A'}`;

  // Display name — replace MAVERICK_COUNTER with Takeaway, same convention used
  // by ReceiptDisplay, PrintReceiptModal, and RecentOrdersModal. Used everywhere
  // below instead of the raw name (screen preview, direct print payload, and the
  // browser-print fallback HTML all reference this single variable).
  const tableName = rawTableName.toUpperCase().includes('MAVERICK_COUNTER')
    ? 'Takeaway'
    : rawTableName;

  const tableSplit = bill.tableSplit || bill.table_split;
  const pax = bill.pax;
  const discountReason = bill.discount_reason || bill.discountReason || '';
  const discountType = discountPercentage > 0 ? 'PERCENTAGE' : 'FIXED';

  // Full bill_number as stored in the DB — truncating it made a reprint disagree
  // with the original slip and with the bills list
  const receiptNumberDisplay = String(billNumber);

  /**
   * 🖨️ Direct Print to Thermal Printer (via Print Agent or backend)
   * Uses receipt configuration from database for formatting
   */
  const handleDirectPrint = async () => {
    setIsPrinting(true);
    try {
      // Generate formatted content using receipt configuration and branch details
      const billData = {
        restaurantName: branchDetails?.name || 'MAVERICK DINE',
        address: branchDetails?.address || null,
        phone: branchDetails?.phone || null,
        gstin: branchDetails?.gstNumber || null,
        fssai: branchDetails?.fssaiNumber || null,
        logoUrl: branchDetails?.logoUrl || null,
        upiId: branchDetails?.upiId || null,
        merchantUpiId: branchDetails?.upiId || null, // For QR code generation
        thankYouMessage: branchDetails?.thankYouMessage || null,
        billNumber: receiptNumberDisplay,
        receiptNumber: receiptNumberDisplay,
        tableName: tableName,
        tableType: bill.tableType || bill.table_type,
        tableSplit: tableSplit,
        pax: pax,
        timestamp: billTimestampRaw,
        orderType: orderTypeLabel,
        items: receiptItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total,
        })),
        subtotal,
        discountAmount,
        discountPercentage,
        discountType,
        taxAmount,
        taxPercentage,
        taxableAmount: bill.taxableAmount || bill.taxable_amount,
        cgstRate: bill.cgstRate || bill.cgst_rate || null,
        sgstRate: bill.sgstRate || bill.sgst_rate || null,
        cgstAmount: bill.cgstAmount || bill.cgst_amount || null,
        sgstAmount: bill.sgstAmount || bill.sgst_amount || null,
        roundOff,
        finalAmount,
        paymentMode: bill.paymentMode || bill.payment_mode,
        cashReceived: bill.cashReceived || bill.cash_received,
        changeDue: bill.changeDue || bill.change_due,
        isReprint: true, // Mark as reprint
      };

      // Generate formatted content based on receipt configuration
      const receiptOutput = generateBillContent(receiptConfig, billData);

      // Check if content was generated
      if (!receiptOutput?.content) {
        console.error('Failed to generate receipt content');
        toast.error('Failed to generate receipt content. Opening browser print...', { duration: 3000 });
        handleBrowserPrint();
        return;
      }

      // Check if Print Agent is available
      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();
      console.log('Print Agent available:', isPrintAgentAvailable, 'Port:', printService.getPrintAgentPort());
      console.log('Formatted content length:', receiptOutput.content?.length);
      console.log('Formatted content preview:', receiptOutput.content?.substring(0, 200));

      if (isPrintAgentAvailable) {
        // Use Print Agent directly for local printing
        const requestBody = {
          content: receiptOutput.content,
          printerId: null,
          logo: receiptOutput.logo,
          qrCode: receiptOutput.qrCode,
        };
        console.log('Sending to Print Agent:', JSON.stringify(requestBody).substring(0, 500));

        const response = await fetch(`http://localhost:${printService.getPrintAgentPort()}/print/bill`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        const result = await response.json();
        console.log('Print Agent response:', result);

        if (result?.success) {
          toast.success('Receipt reprinted successfully!', { duration: 2000 });
          setTimeout(() => onClose(), 500);
        } else {
          console.warn('Direct print failed:', result?.error || result?.message);
          toast.error(`Print failed: ${result?.error || result?.message || 'Unknown error'}. Opening browser print...`, { duration: 3000 });
          handleBrowserPrint();
        }
      } else {
        // Fall back to server printing via testPrintTemplate
        const response = await printService.testPrintTemplate(
          receiptOutput.content,
          'BILL',
          null,
          { logo: receiptOutput.logo, qrCode: receiptOutput.qrCode }
        );

        if (response?.success) {
          toast.success('Receipt reprinted successfully!', { duration: 2000 });
          setTimeout(() => onClose(), 500);
        } else {
          console.warn('Server print failed:', response?.message || response?.error);
          toast.error(`Print failed: ${response?.message || response?.error || 'Unknown error'}. Opening browser print...`, { duration: 3000 });
          handleBrowserPrint();
        }
      }
    } catch (error) {
      console.error('Direct print error:', error);
      toast.error('Print unavailable. Opening browser print...', { duration: 3000 });
      handleBrowserPrint();
    } finally {
      setIsPrinting(false);
    }
  };

  // Browser Print Handler (fallback)
  const handleBrowserPrint = () => {
    const printWindow = window.open('', '', 'width=800,height=600');

    // Check if popup was blocked
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site, then try again.');
      return;
    }

    const receiptHTML = generateReceiptHTML();
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  // Generate Receipt HTML
  const generateReceiptHTML = () => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Receipt - ${billNumber}</title>
          <style>
            /* Reset and Base Styles */
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            /* Screen Preview Styles */
            @media screen {
              body {
                font-family: 'Courier New', Courier, monospace;
                background: #f5f5f5;
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                min-height: 100vh;
              }

              .receipt-container {
                background: white;
                width: ${PRINTER_CONFIG.width};
                max-width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                border: 1px solid #ddd;
              }
            }

            /* Print Styles for Thermal Printer */
            @media print {
              @page {
                size: ${PRINTER_CONFIG.width} auto;
                margin: 0;
              }

              body {
                margin: 0;
                padding: 0;
                width: ${PRINTER_CONFIG.width};
                background: white;
              }

              .receipt-container {
                width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                ${PRINT_SETTINGS.enableAutoCut ? `padding-bottom: ${PRINT_SETTINGS.bottomMargin};` : ''}
                page-break-after: auto;
              }

              .no-print {
                display: none !important;
              }

              .item-row {
                page-break-inside: avoid;
              }
            }

            /* Common Styles */
            .receipt-container {
              font-family: 'Courier New', Courier, monospace;
              font-size: ${PRINTER_CONFIG.fontSize};
              line-height: ${PRINTER_CONFIG.lineHeight};
              color: #000;
            }

            /* Typography */
            .text-center { text-align: center; }
            .text-left { text-align: left; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .text-sm { font-size: 0.9em; }
            .text-xs { font-size: 0.8em; }
            .text-lg { font-size: 1.2em; }
            .text-xl { font-size: 1.4em; }

            /* Spacing */
            .mb-1 { margin-bottom: 0.25em; }
            .mb-2 { margin-bottom: 0.5em; }
            .mb-3 { margin-bottom: 0.75em; }
            .mb-4 { margin-bottom: 1em; }
            .mt-1 { margin-top: 0.25em; }
            .mt-2 { margin-top: 0.5em; }
            .mt-3 { margin-top: 0.75em; }
            .mt-4 { margin-top: 1em; }
            .pb-2 { padding-bottom: 0.5em; }
            .pt-2 { padding-top: 0.5em; }
            .py-1 { padding-top: 0.25em; padding-bottom: 0.25em; }

            /* Borders */
            .border-dashed {
              border-style: dashed;
              border-color: #000;
            }
            .border-t { border-top: 1px solid #000; }
            .border-t-2 { border-top: 2px solid #000; }
            .border-b { border-bottom: 1px solid #000; }
            .border-b-2 { border-bottom: 2px solid #000; }
            .border-dashed-t { border-top: 2px dashed #000; }
            .border-dashed-b { border-bottom: 2px dashed #000; }

            /* Layout */
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .items-center { align-items: center; }
            .w-full { width: 100%; }

            /* Specific Receipt Elements */
            .header {
              text-align: center;
              margin-bottom: 1em;
              padding-bottom: 1em;
              border-bottom: 2px dashed #000;
            }

            .company-name {
              font-size: 1.4em;
              font-weight: bold;
              margin-bottom: 0.25em;
              letter-spacing: 0.05em;
            }

            .company-subtitle {
              font-size: 0.8em;
              margin-bottom: 0.5em;
            }

            .receipt-number {
              font-size: 0.8em;
              margin-top: 0.5em;
            }

            .reprint-badge {
              font-size: 0.9em;
              font-weight: bold;
              margin-top: 0.5em;
              color: #666;
              background: #f0f0f0;
              padding: 0.25em 0.5em;
              border-radius: 3px;
              display: inline-block;
            }

            .info-section {
              margin-bottom: 1em;
              padding-bottom: 0.75em;
              border-bottom: 1px solid #000;
            }

            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.25em;
              font-size: 0.9em;
            }

            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 0.5em;
              padding-bottom: 0.5em;
              border-bottom: 1px solid #000;
              font-size: 0.9em;
            }

            .items-header span:nth-child(1) { flex: 2; }
            .items-header span:nth-child(2) { width: 2.5em; text-align: center; }
            .items-header span:nth-child(3) { width: 5em; text-align: right; }

            .item-row {
              margin-bottom: 0.75em;
            }

            .item-main {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.2em;
            }

            .item-main span:nth-child(1) {
              flex: 2;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .item-main span:nth-child(2) {
              width: 2.5em;
              text-align: center;
              font-weight: bold;
            }
            .item-main span:nth-child(3) {
              width: 5em;
              text-align: right;
              font-weight: bold;
            }

            .item-price {
              font-size: 0.8em;
              color: #333;
              margin-left: 0.5em;
            }

            .totals-section {
              border-top: 1px solid #000;
              padding-top: 0.75em;
              margin-top: 1em;
              margin-bottom: 1em;
            }

            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.4em;
              font-size: 0.9em;
            }

            .discount-row {
              color: #2d7a2d;
            }

            .final-total {
              border-top: 2px dashed #000;
              padding-top: 0.75em;
              margin-top: 0.75em;
            }

            .final-total-row {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              font-size: 1.1em;
              padding: 0.5em;
              background: #f0f0f0;
              border-radius: 3px;
            }

            .footer {
              text-align: center;
              margin-top: 1.5em;
              padding-top: 1em;
              border-top: 2px dashed #000;
              font-size: 0.8em;
            }

            .thank-you {
              font-weight: bold;
              margin-bottom: 0.5em;
              font-size: 1.1em;
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <!-- Header -->
            <div class="header">
              ${receiptConfig.showLogo && branchDetails?.logoUrl ? `<div class="text-center mb-2"><img src="${branchDetails.logoUrl}" alt="Logo" style="max-height: 60px; max-width: 150px; margin: 0 auto;" /></div>` : ''}
              ${receiptConfig.showRestaurantName ? `<div class="company-name">${branchDetails?.name || 'MAVERICK DINE'}</div>` : ''}
              ${receiptConfig.showAddress && branchDetails?.address ? `<div class="company-subtitle">${branchDetails.address}</div>` : ''}
              ${receiptConfig.showPhone && branchDetails?.phone ? `<div class="text-xs">Ph: ${branchDetails.phone}</div>` : ''}
              ${receiptConfig.showGstin && branchDetails?.gstNumber ? `<div class="text-xs">GSTIN: ${branchDetails.gstNumber}</div>` : ''}
              ${receiptConfig.showFssai && branchDetails?.fssaiNumber ? `<div class="text-xs">FSSAI: ${branchDetails.fssaiNumber}</div>` : ''}
              <div class="receipt-number">Receipt #${receiptNumberDisplay}</div>
              <div class="reprint-badge">*** REPRINT ***</div>
            </div>

            <!-- Info Section -->
            <div class="info-section">
              <div class="info-row">
                <span class="font-bold">Table:</span>
                <span class="font-bold">${tableName}</span>
              </div>
              <div class="info-row text-xs">
                <span>Date/Time:</span>
                <span>${timestamp}</span>
              </div>
              ${pax ? `
              <div class="info-row">
                <span>Guests:</span>
                <span>${pax}</span>
              </div>
              ` : ''}
            </div>

            <!-- Items -->
            <div class="items-header">
              <span>Item</span>
              <span>Qty</span>
              <span>Amount</span>
            </div>

            ${receiptItems.map(item => `
              <div class="item-row">
                <div class="item-main">
                  <span>${item.name}</span>
                  <span>${item.quantity}</span>
                  <span>₹${item.total.toFixed(2)}</span>
                </div>
                <div class="item-price">@ ₹${item.price.toFixed(2)}</div>
              </div>
            `).join('')}

            <!-- Totals -->
            <div class="totals-section">
              ${receiptConfig.showSubtotal ? `
              <div class="total-row">
                <span>Subtotal:</span>
                <span>₹${subtotal.toFixed(2)}</span>
              </div>
              ` : ''}
              ${receiptConfig.showDiscountTotal && discountAmount > 0 ? `
              <div class="total-row discount-row">
                <span>Discount ${discountType === 'PERCENTAGE' ? `(${discountPercentage}%)` : `(₹${discountAmount.toFixed(2)})`}:</span>
                <span>- ₹${discountAmount.toFixed(2)}</span>
              </div>
              ` : ''}
              ${receiptConfig.showTaxBreakdown && taxPercentage > 0 ? `
              <div class="total-row">
                <span>Taxable Amount:</span>
                <span>₹${taxableAmount.toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>GST (${taxPercentage}%):</span>
                <span>₹${taxAmount.toFixed(2)}</span>
              </div>
              ` : ''}
              <div class="total-row">
                <span>Round Off:</span>
                <span>${roundOff >= 0 ? '+' : ''}₹${roundOff.toFixed(2)}</span>
              </div>
            </div>

            <!-- Final Total -->
            ${receiptConfig.showGrandTotal ? `
            <div class="final-total">
              <div class="final-total-row">
                <span>TOTAL:</span>
                <span>₹${finalAmount.toFixed(2)}</span>
              </div>
            </div>
            ` : ''}

            <!-- Footer -->
            <div class="footer">
              ${receiptConfig.showThankYou ? `<div class="thank-you">${receiptConfig.thankYouText || branchDetails?.thankYouMessage || 'Thank you! Visit Again!'}</div>` : ''}
              ${receiptConfig.footerText ? `<div class="text-xs">${receiptConfig.footerText}</div>` : ''}
              <div class="text-xs">${receiptConfig?.printerPoweredBy || 'A Product by Maverick Ignite'}</div>
              ${PRINT_SETTINGS.includeDiscountReason && discountAmount > 0 && discountReason ? `
              <div class="text-xs mt-2">Discount Reason: ${discountReason}</div>
              ` : ''}
            </div>
          </div>

          <script>
            // Auto-print on load
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, ${PRINT_SETTINGS.autoPrintDelay});
            };

            // Close window after print or cancel
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `
            window.onafterprint = function() {
              setTimeout(function() {
                window.close();
              }, ${PRINT_SETTINGS.autoCloseDelay});
            };
            ` : ''}
          </script>
        </body>
      </html>
    `;
  };

  // Thermal Print Handler (legacy - now calls direct print first)
  const handleThermalPrint = () => {
    handleDirectPrint();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-white">Reprint Receipt</h3>
            <p className="text-sm text-teal-100 mt-0.5">Bill #{billNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Receipt Preview */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4 font-mono text-sm">
            {/* Company Header */}
            <div className="text-center border-b-2 border-dashed border-slate-300 pb-3 mb-3">
              {receiptConfig.showLogo && branchDetails?.logoUrl && (
                <img src={branchDetails.logoUrl} alt="Logo" className="mx-auto mb-2" style={{ maxHeight: '50px', maxWidth: '120px' }} />
              )}
              {receiptConfig.showRestaurantName && (
                <div className="font-bold text-base">{branchDetails?.name || 'MAVERICK DINE'}</div>
              )}
              {receiptConfig.showAddress && branchDetails?.address && (
                <div className="text-xs text-slate-600">{branchDetails.address}</div>
              )}
              {receiptConfig.showPhone && branchDetails?.phone && (
                <div className="text-xs text-slate-600">Ph: {branchDetails.phone}</div>
              )}
              {receiptConfig.showGstin && branchDetails?.gstNumber && (
                <div className="text-xs text-slate-500">GSTIN: {branchDetails.gstNumber}</div>
              )}
              {receiptConfig.showFssai && branchDetails?.fssaiNumber && (
                <div className="text-xs text-slate-500">FSSAI: {branchDetails.fssaiNumber}</div>
              )}
              <div className="text-xs mt-2">Receipt #{receiptNumberDisplay}</div>
              <div className="inline-block bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold mt-2">
                *** REPRINT ***
              </div>
            </div>

            {/* Bill Info */}
            <div className="text-xs space-y-1 border-b border-slate-200 pb-2 mb-3">
              <div className="flex justify-between">
                <span className="font-semibold">Table:</span>
                <span className="font-semibold">{tableName}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Date/Time:</span>
                <span>{timestamp}</span>
              </div>
              {pax && (
                <div className="flex justify-between">
                  <span>Guests:</span>
                  <span>{pax}</span>
                </div>
              )}
            </div>

            {/* Items Header */}
            <div className="flex justify-between font-semibold text-xs border-b border-slate-300 pb-1 mb-2">
              <span className="flex-1">Item</span>
              <span className="w-12 text-center">Qty</span>
              <span className="w-20 text-right">Amount</span>
            </div>

            {/* Items */}
            <div className="space-y-2 mb-3">
              {receiptItems.map((item, idx) => (
                <div key={idx} className="text-xs">
                  <div className="flex justify-between">
                    <span className="flex-1 truncate">{item.name}</span>
                    <span className="w-12 text-center font-semibold">{item.quantity}</span>
                    <span className="w-20 text-right font-semibold">₹{item.total.toFixed(2)}</span>
                  </div>
                  <div className="text-slate-500 ml-1">@ ₹{item.price.toFixed(2)}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-slate-300 pt-2 space-y-1 text-xs">
              {receiptConfig.showSubtotal && (
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
              )}
              {receiptConfig.showDiscountTotal && discountAmount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount {discountType === 'PERCENTAGE' ? `(${discountPercentage}%)` : `(₹${discountAmount.toFixed(2)})`}:</span>
                  <span>- ₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              {receiptConfig.showTaxBreakdown && taxPercentage > 0 && (
                <>
                  <div className="flex justify-between">
                    <span>Taxable Amount:</span>
                    <span>₹{taxableAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({taxPercentage}%):</span>
                    <span>₹{taxAmount.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span>Round Off:</span>
                <span>{roundOff >= 0 ? '+' : ''}₹{roundOff.toFixed(2)}</span>
              </div>
            </div>

            {/* Final Total */}
            {receiptConfig.showGrandTotal && (
              <div className="border-t-2 border-dashed border-slate-400 mt-2 pt-2">
                <div className="flex justify-between font-bold bg-slate-100 p-2 rounded">
                  <span>TOTAL:</span>
                  <span className="text-base">₹{finalAmount.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* QR Code Section */}
            {receiptConfig.showQrCode && (
              <div className="border-t border-dashed border-slate-300 mt-3 pt-3 text-center">
                <p className="text-xs font-semibold text-slate-600 mb-2">SCAN TO PAY</p>
                {branchDetails?.upiId ? (
                  <div className="bg-slate-100 p-3 rounded-lg inline-block">
                    <div className="w-24 h-24 bg-white border-2 border-slate-300 rounded flex items-center justify-center mx-auto mb-2">
                      <span className="text-[10px] text-slate-400 text-center">QR Code<br/>on print</span>
                    </div>
                    <p className="text-xs text-slate-600">UPI: {branchDetails.upiId}</p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700 font-medium">[QR Code Not Available]</p>
                    <p className="text-[10px] text-amber-600 mt-1">Configure UPI ID in Branch Settings</p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="text-center border-t-2 border-dashed border-slate-300 mt-3 pt-3 text-xs">
              {receiptConfig.showThankYou && (
                <div className="font-semibold mb-1">{receiptConfig.thankYouText || branchDetails?.thankYouMessage || 'Thank you! Visit Again!'}</div>
              )}
              {receiptConfig.footerText && (
                <div className="text-slate-500">{receiptConfig.footerText}</div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-slate-200 p-4 bg-white flex gap-3">
          <button
            onClick={onClose}
            disabled={isPrinting}
            className="flex-1 px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleThermalPrint}
            disabled={isPrinting || isLoading}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold rounded-lg transition-all hover:scale-105 flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:hover:scale-100"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Loading...</span>
              </>
            ) : isPrinting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Printing...</span>
              </>
            ) : (
              <>
                <Printer size={18} />
                <span>Print Receipt</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}